// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { defaults, element, elementAttributes, queryTableNotFoundErr, queryTableXmlPath, sheetsNotFoundErr, sheetsXmlPath, tableNotFoundErr, tableXmlPath, textResultType, workbookXmlPath, xmlTextResultType, } from "./constants";
import documentUtils from "./documentUtils";
import { v4 } from "uuid";
var updateTableInitialDataIfNeeded = function (zip, tableData, updateQueryTable) { return __awaiter(void 0, void 0, void 0, function () {
    var sheetsXmlString, newSheet, queryTableXmlString, newQueryTable, workbookXmlString, newWorkbook, tableXmlString, newTable;
    var _a, _b, _c, _d;
    return __generator(this, function (_e) {
        switch (_e.label) {
            case 0:
                if (!tableData) {
                    return [2 /*return*/];
                }
                return [4 /*yield*/, ((_a = zip.file(sheetsXmlPath)) === null || _a === void 0 ? void 0 : _a.async(textResultType))];
            case 1:
                sheetsXmlString = _e.sent();
                if (sheetsXmlString === undefined) {
                    throw new Error(sheetsNotFoundErr);
                }
                newSheet = updateSheetsInitialData(sheetsXmlString, tableData);
                zip.file(sheetsXmlPath, newSheet);
                if (!updateQueryTable) return [3 /*break*/, 5];
                return [4 /*yield*/, ((_b = zip.file(queryTableXmlPath)) === null || _b === void 0 ? void 0 : _b.async(textResultType))];
            case 2:
                queryTableXmlString = _e.sent();
                if (queryTableXmlString === undefined) {
                    throw new Error(queryTableNotFoundErr);
                }
                return [4 /*yield*/, updateQueryTablesInitialData(queryTableXmlString, tableData)];
            case 3:
                newQueryTable = _e.sent();
                zip.file(queryTableXmlPath, newQueryTable);
                return [4 /*yield*/, ((_c = zip.file(workbookXmlPath)) === null || _c === void 0 ? void 0 : _c.async(textResultType))];
            case 4:
                workbookXmlString = _e.sent();
                if (workbookXmlString === undefined) {
                    throw new Error(sheetsNotFoundErr);
                }
                newWorkbook = updateWorkbookInitialData(workbookXmlString, tableData);
                zip.file(workbookXmlPath, newWorkbook);
                _e.label = 5;
            case 5: return [4 /*yield*/, ((_d = zip.file(tableXmlPath)) === null || _d === void 0 ? void 0 : _d.async(textResultType))];
            case 6:
                tableXmlString = _e.sent();
                if (tableXmlString === undefined) {
                    throw new Error(tableNotFoundErr);
                }
                newTable = updateTablesInitialData(tableXmlString, tableData, updateQueryTable);
                zip.file(tableXmlPath, newTable);
                return [2 /*return*/];
        }
    });
}); };
var updateTablesInitialData = function (tableXmlString, tableData, updateQueryTable) {
    if (updateQueryTable === void 0) { updateQueryTable = false; }
    var parser = new DOMParser();
    var serializer = new XMLSerializer();
    var tableDoc = parser.parseFromString(tableXmlString, xmlTextResultType);
    var tableColumns = tableDoc.getElementsByTagName(element.tableColumns)[0];
    tableColumns.textContent = "";
    tableData.columnNames.forEach(function (column, columnIndex) {
        var tableColumn = tableDoc.createElementNS(tableDoc.documentElement.namespaceURI, element.tableColumn);
        tableColumn.setAttribute(elementAttributes.id, (columnIndex + 1).toString());
        tableColumn.setAttribute(elementAttributes.name, column);
        tableColumns.appendChild(tableColumn);
        tableColumn.setAttribute(elementAttributes.xr3uid, "{" + v4().toUpperCase() + "}");
        if (updateQueryTable) {
            tableColumn.setAttribute(elementAttributes.uniqueName, (columnIndex + 1).toString());
            tableColumn.setAttribute(elementAttributes.queryTableFieldId, (columnIndex + 1).toString());
        }
    });
    tableColumns.setAttribute(elementAttributes.count, tableData.columnNames.length.toString());
    tableDoc
        .getElementsByTagName(element.table)[0]
        .setAttribute(elementAttributes.reference, "A1:".concat(documentUtils.getCellReferenceRelative(tableData.columnNames.length - 1, tableData.rows.length + 1)));
    tableDoc
        .getElementsByTagName(element.autoFilter)[0]
        .setAttribute(elementAttributes.reference, "A1:".concat(documentUtils.getCellReferenceRelative(tableData.columnNames.length - 1, tableData.rows.length + 1)));
    return serializer.serializeToString(tableDoc);
};
var updateWorkbookInitialData = function (workbookXmlString, tableData) {
    var newParser = new DOMParser();
    var newSerializer = new XMLSerializer();
    var workbookDoc = newParser.parseFromString(workbookXmlString, xmlTextResultType);
    var definedName = workbookDoc.getElementsByTagName(element.definedName)[0];
    definedName.textContent =
        defaults.sheetName + "!$A$1:".concat(documentUtils.getCellReferenceAbsolute(tableData.columnNames.length - 1, tableData.rows.length + 1));
    return newSerializer.serializeToString(workbookDoc);
};
var updateQueryTablesInitialData = function (queryTableXmlString, tableData) {
    var parser = new DOMParser();
    var serializer = new XMLSerializer();
    var queryTableDoc = parser.parseFromString(queryTableXmlString, xmlTextResultType);
    var queryTableFields = queryTableDoc.getElementsByTagName(element.queryTableFields)[0];
    queryTableFields.textContent = "";
    tableData.columnNames.forEach(function (column, columnIndex) {
        var queryTableField = queryTableDoc.createElementNS(queryTableDoc.documentElement.namespaceURI, element.queryTableField);
        queryTableField.setAttribute(elementAttributes.id, (columnIndex + 1).toString());
        queryTableField.setAttribute(elementAttributes.name, column);
        queryTableField.setAttribute(elementAttributes.tableColumnId, (columnIndex + 1).toString());
        queryTableFields.appendChild(queryTableField);
    });
    queryTableFields.setAttribute(elementAttributes.count, tableData.columnNames.length.toString());
    queryTableDoc.getElementsByTagName(element.queryTableRefresh)[0].setAttribute(elementAttributes.nextId, (tableData.columnNames.length + 1).toString());
    return serializer.serializeToString(queryTableDoc);
};
var updateSheetsInitialData = function (sheetsXmlString, tableData) {
    var parser = new DOMParser();
    var serializer = new XMLSerializer();
    var sheetsDoc = parser.parseFromString(sheetsXmlString, xmlTextResultType);
    var sheetData = sheetsDoc.getElementsByTagName(element.sheetData)[0];
    sheetData.textContent = "";
    var rowIndex = 0;
    var columnRow = sheetsDoc.createElementNS(sheetsDoc.documentElement.namespaceURI, element.row);
    columnRow.setAttribute(elementAttributes.row, (rowIndex + 1).toString());
    columnRow.setAttribute(elementAttributes.spans, "1:" + tableData.columnNames.length);
    columnRow.setAttribute(elementAttributes.x14acDyDescent, "0.3");
    tableData.columnNames.forEach(function (col, colIndex) {
        columnRow.appendChild(documentUtils.createCell(sheetsDoc, colIndex, rowIndex, col));
    });
    sheetData.appendChild(columnRow);
    rowIndex++;
    tableData.rows.forEach(function (row) {
        var newRow = sheetsDoc.createElementNS(sheetsDoc.documentElement.namespaceURI, element.row);
        newRow.setAttribute(elementAttributes.row, (rowIndex + 1).toString());
        newRow.setAttribute(elementAttributes.spans, "1:" + row.length);
        newRow.setAttribute(elementAttributes.x14acDyDescent, "0.3");
        row.forEach(function (cellContent, colIndex) {
            newRow.appendChild(documentUtils.createCell(sheetsDoc, colIndex, rowIndex, cellContent));
        });
        sheetData.appendChild(newRow);
        rowIndex++;
    });
    var reference = documentUtils.getTableReference(tableData.rows[0].length - 1, tableData.rows.length + 1);
    sheetsDoc.getElementsByTagName(element.dimension)[0].setAttribute(elementAttributes.reference, reference);
    sheetsDoc.getElementsByTagName(element.selection)[0].setAttribute(elementAttributes.sqref, reference);
    return serializer.serializeToString(sheetsDoc);
};
export default {
    updateTableInitialDataIfNeeded: updateTableInitialDataIfNeeded,
    updateSheetsInitialData: updateSheetsInitialData,
    updateWorkbookInitialData: updateWorkbookInitialData,
    updateTablesInitialData: updateTablesInitialData,
    updateQueryTablesInitialData: updateQueryTablesInitialData,
};
